import React from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';

import { FormatIcon, Button, CenteredModal } from '@app/core';
import {
  ModeratedTaskSetting,
  Session,
  SessionFormat,
  useTaskSetting,
} from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  sectionContainer: {
    marginTop: theme.marginBase * 4,
  },
  firstLine: {
    display: 'flex',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  title: {
    ...theme.fonts.h5,
    flex: 1,
    marginLeft: theme.marginBase * 2,
  },
  details: {
    ...theme.fonts.body,
    marginLeft: theme.marginBase * 6,
    color: theme.colors.inkSecondary,
  },
  emptyContent: {
    textAlign: 'center',
  },
  emptyTitle: {
    ...theme.fonts.h5,
    marginBottom: theme.marginBase * 2,
    marginTop: theme.marginBase * 4,
  },
  emptyButtonIcon: {
    marginRight: theme.marginBase,
    fontSize: 22,
  },
}));

interface Props {
  isOpen: boolean;
  onRequestClose?: () => any;
  session: Session;
}

export const FormatDetailsModal = (props: Props) => {
  const { t } = useTranslation();
  const { session } = props;
  const theme: Theme = useTheme();
  const classes = useStyles({ ...props, theme });
  const { data: taskSetting } = useTaskSetting<ModeratedTaskSetting>(
    session?.taskSettingId,
  );

  return (
    <CenteredModal
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
      title={t('sessions.details.formatDetailModal.title')}
    >
      {session.format === SessionFormat.PHYSICAL_MEETING && (
        <section className={classes.sectionContainer}>
          <div className={classes.firstLine}>
            <FormatIcon format={SessionFormat.PHYSICAL_MEETING} />
            <h4 className={classes.title}>{taskSetting?.address}</h4>
          </div>
          <p className={classes.details}>{taskSetting?.addressDetails}</p>
        </section>
      )}
      {session.format === SessionFormat.PHONE_CALL && (
        <section className={classes.sectionContainer}>
          <div className={classes.firstLine}>
            <FormatIcon format={SessionFormat.PHONE_CALL} />
            <h4 className={classes.title}>
              <Trans i18nKey="sessions.details.formatDetailModal.phoneCall" />
            </h4>
          </div>
          <p className={classes.details}>
            <Trans
              i18nKey="sessions.details.formatDetailModal.phoneCallDetails"
              values={session}
            />
          </p>
        </section>
      )}
      {session.format === SessionFormat.VIDEO_CALL && (
        <section className={classes.sectionContainer}>
          <div className={classes.firstLine}>
            <FormatIcon format={SessionFormat.VIDEO_CALL} />
            <h4 className={classes.title}>
              <Trans i18nKey="sessions.details.formatDetailModal.videoCall" />
            </h4>
          </div>
          <p className={classes.details}>{taskSetting?.videoCallDetails}</p>
        </section>
      )}
      {session.format === SessionFormat.IN_PARTICIPANT_PLACE && (
        <section className={classes.sectionContainer}>
          <div className={classes.firstLine}>
            <FormatIcon format={SessionFormat.IN_PARTICIPANT_PLACE} />
            <h4 className={classes.title}>
              <Trans i18nKey="sessions.details.formatDetailModal.inParticipantPlace" />
            </h4>
          </div>
          <p className={classes.details}>
            <Trans i18nKey="sessions.details.formatDetailModal.inParticipantPlaceDetails" />
          </p>
        </section>
      )}
      {!session.format && (
        <div className={classes.emptyContent}>
          <h4 className={classes.emptyTitle}>
            <Trans i18nKey="sessions.details.formatDetailModal.emptyTitle" />
          </h4>
          <Button
            to={`/sessions/edit/${session.id}#location`}
            color="blue"
            variant="muted"
          >
            <i
              className={classnames(classes.emptyButtonIcon, 'ri-edit-line')}
            />
            <Trans i18nKey="sessions.details.formatDetailModal.emptyButton" />
          </Button>
        </div>
      )}
    </CenteredModal>
  );
};
